'use client';

import { useState, type CSSProperties, type FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { COMMENT_CATEGORIES, CATEGORY_LABELS, COMMENT_LIMITS } from '@/lib/consultations';

/**
 * Public comment form. Posts to /api/consultations/[id]/comments, which owns
 * every rule (open window, demonstration refusal, rate limit, CSRF, lengths).
 * The form repeats only the cheap client-side checks so a reader gets an
 * immediate answer; the server's answer is the one that counts.
 *
 * The email address is required — the programme must be able to reach a
 * commenter about their comment — but it is never displayed: the column is
 * not readable by anonymous or ordinary authenticated users (see migration
 * public_consultation_module).
 */
export function CommentForm({ consultationId }: { consultationId: string }) {
  const router = useRouter();
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');
  const [form, setForm] = useState({ authorName: '', authorEmail: '', authorOrg: '', category: 'GENERAL', content: '' });

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  async function submit() {
    setState('sending');
    setMessage('');
    try {
      const res = await fetch(`/api/consultations/${encodeURIComponent(consultationId)}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setState('error');
        setMessage(body.error || `Submission failed (${res.status}).`);
        return;
      }
      setState('done');
      setForm({ authorName: '', authorEmail: '', authorOrg: '', category: 'GENERAL', content: '' });
      router.refresh();
    } catch {
      setState('error');
      setMessage('Network error — your comment was not sent. Please try again.');
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ background: 'white', border: '1px solid var(--mcc-border)', borderRadius: 8, padding: 24 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--mcc-navy)', margin: '0 0 6px' }}>Submit a comment</h2>
      <p style={{ fontSize: 14, color: 'var(--mcc-muted)', margin: '0 0 18px', lineHeight: 1.55 }}>
        Your name, organisation and comment are published. Your email address is not published; the programme
        uses it only to respond to you about this comment.
      </p>

      {state === 'done' && (
        <div role="status" style={{ ...notice, background: 'var(--mcc-success-bg)', color: 'var(--mcc-sea)' }}>
          Thank you. Your comment is now part of the public record for this consultation.
        </div>
      )}
      {state === 'error' && (
        <div role="alert" style={{ ...notice, background: 'var(--mcc-danger-bg, #f7e2e0)', color: '#842029' }}>{message}</div>
      )}

      <div style={grid2}>
        <label style={label}>Full name *
          <input required maxLength={COMMENT_LIMITS.authorName} value={form.authorName} onChange={set('authorName')} style={input} autoComplete="name" />
        </label>
        <label style={label}>Email *
          <input required type="email" maxLength={COMMENT_LIMITS.authorEmail} value={form.authorEmail} onChange={set('authorEmail')} style={input} autoComplete="email" />
        </label>
      </div>
      <div style={grid2}>
        <label style={label}>Organisation
          <input maxLength={COMMENT_LIMITS.authorOrg} value={form.authorOrg} onChange={set('authorOrg')} style={input} autoComplete="organization" />
        </label>
        <label style={label}>Topic
          <select value={form.category} onChange={set('category')} style={input}>
            {COMMENT_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
          </select>
        </label>
      </div>
      <label style={{ ...label, marginBottom: 16 }}>Comment *
        <textarea required rows={6} maxLength={COMMENT_LIMITS.content} value={form.content} onChange={set('content')} style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }} />
        <span style={{ fontSize: 12, color: 'var(--mcc-muted)', fontWeight: 400 }}>{form.content.length} / {COMMENT_LIMITS.content}</span>
      </label>
      <button type="submit" disabled={state === 'sending'} style={button}>
        {state === 'sending' ? 'Sending…' : 'Submit comment'}
      </button>
    </form>
  );
}

const grid2: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 };
const label: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: 'var(--mcc-muted)' };
const input: CSSProperties = { padding: '10px 12px', border: '1px solid var(--mcc-border)', borderRadius: 4, fontSize: 15, color: 'var(--mcc-ink)', background: 'white', width: '100%' };
const notice: CSSProperties = { padding: '10px 14px', borderRadius: 6, fontSize: 14, marginBottom: 16 };
const button: CSSProperties = { background: 'var(--mcc-navy)', color: 'white', border: 'none', borderRadius: 4, padding: '12px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer' };
